import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", referral_code: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [parrainName, setParrainName] = useState(null);
  const [refError, setRefError] = useState("");
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Pre-fill referral_code from URL ?ref=XXXXXX
  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) set("referral_code", ref.toUpperCase());
  }, [searchParams]);

  // Validate referral code as user types (debounced)
  useEffect(() => {
    const code = (form.referral_code || "").trim().toUpperCase();
    setRefError("");
    setParrainName(null);
    if (code.length < 4) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/referral/validate/${encodeURIComponent(code)}`);
        setParrainName(data.parrain_first_name || "votre parrain");
      } catch {
        setRefError("Code de parrainage invalide");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.referral_code]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const payload = { ...form };
      if (!payload.referral_code) delete payload.referral_code;
      await register(payload);
      toast.success(parrainName
        ? `Compte créé. Bienvenue ! −5% sur votre première commande grâce à ${parrainName}.`
        : "Compte créé. Bienvenue !");
      const next = searchParams.get("next");
      nav(next && next.startsWith("/") ? next : "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-20" data-testid="register-page">
      <h1 className="font-display text-4xl mb-8 text-center">Créer un compte</h1>
      <form onSubmit={submit} className="space-y-5 rounded-xl border border-border bg-card p-8" data-testid="register-form">
        <div>
          <Label htmlFor="name">Nom complet</Label>
          <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-input mt-2" data-testid="reg-name" />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="bg-input mt-2" data-testid="reg-email" />
        </div>
        <div>
          <Label htmlFor="phone">Téléphone (optionnel)</Label>
          <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-input mt-2" data-testid="reg-phone" />
        </div>
        <div>
          <Label htmlFor="password">Mot de passe (min. 6 car.)</Label>
          <Input id="password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="bg-input mt-2" data-testid="reg-password" />
        </div>
        <div>
          <Label htmlFor="referral_code" className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            Code de parrainage <span className="text-xs text-muted-foreground">(optionnel)</span>
          </Label>
          <Input
            id="referral_code"
            value={form.referral_code}
            onChange={(e) => set("referral_code", e.target.value.toUpperCase())}
            placeholder="Ex. ABCDEF"
            maxLength={8}
            className="bg-input mt-2 uppercase tracking-widest"
            data-testid="reg-referral-code"
          />
          {parrainName && (
            <div className="text-xs text-primary mt-2" data-testid="reg-referral-valid">
              Parrainé par <b>{parrainName}</b> · −5% offerts sur votre 1<sup>re</sup> commande.
            </div>
          )}
          {refError && (
            <div className="text-xs text-destructive mt-2" data-testid="reg-referral-error">{refError}</div>
          )}
        </div>
        {error && <div className="text-sm text-destructive" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full h-12" data-testid="register-submit-btn">
          {submitting ? "Création…" : "Créer mon compte"}
        </Button>
        <div className="text-sm text-center text-muted-foreground">
          Déjà inscrit ? <Link to={searchParams.get("next") ? `/connexion?next=${encodeURIComponent(searchParams.get("next"))}` : "/connexion"} className="gold-text hover:underline" data-testid="link-login">Se connecter</Link>
        </div>
      </form>
    </div>
  );
}
